import { Flex } from '@chakra-ui/react';
import { FormFieldFocusSelect } from '@publishing-engine/domain/types/article';
import React, { useState } from 'react';
import { FieldHeader } from '../common/FieldHeader';
import { FocusIssueSelectDisplay } from './FocusIssueSelectDisplay';
import { FocusIssueSelectDiffModal } from './FocusIssueSelectDiffModal';
import { FocusIssueSelectEmptyState } from './FocusIssueSelectEmptyState';
import FocusIssueSelectUpdateModal from './FocusIssueSelectUpdateModal';
import { IAvailableWorkflowActions } from '@publishing-engine/domain/models';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
import useFocusIssueConfig from 'src/hooks/useFocusIssue';
import { EntryPoint } from 'src/utils/types';
import { useSubmissionFields } from 'src/hooks/useSubmissionFields';

type IProps = {
  formFieldDefinition: FormFieldFocusSelect;
  value: string;
  previousValue?: string | null;
  availableActions: IAvailableWorkflowActions;
  onChange?: (value: string) => void;
  entryPoint: EntryPoint;
  submissionContextId: string;
  isUpdateRequired?:boolean
};

export const GenericFocusIssueSelectField: React.FC<IProps> = ({
  formFieldDefinition,
  value,
  previousValue,
  availableActions,
  onChange,
  entryPoint,
  isUpdateRequired
}) => {
  const { checkFormFieldValueIsUpdated } =
    useSubmissionFields(entryPoint);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [showDiffModal, setShowDiffModal] = useState<boolean>(false);
  const openDiffModal = () => setShowDiffModal(true);
  const closeDiffModal = () => setShowDiffModal(false);
  const { submissionContextId } = useSubmissionData();
  const isEditable = !!onChange;

  const editCallback = isEditable
    ? () => {
        setIsEditing(true);
      }
    : undefined;

  const { selectedFocusIssue, ...config } = useFocusIssueConfig(
    submissionContextId,
    value
  );

  const isUpdated = checkFormFieldValueIsUpdated(formFieldDefinition.name);

  return (
    <Flex flexDirection={'column'} gap={2}>
      {isEditing && onChange && formFieldDefinition.name === 'FocusIssue' && (
        <FocusIssueSelectUpdateModal
          formField={formFieldDefinition}
          isOpen={isEditing}
          onCancel={() => {
            setIsEditing(false);
          }}
          value={value}
          onSubmit={(value: string) => {
            setIsEditing(false);
            onChange(value);
          }}
          config={config}
        />
      )}

      {isUpdated && showDiffModal ? (
        <FocusIssueSelectDiffModal
          fieldName={formFieldDefinition.params.labels.main}
          value={value}
          previousValue={previousValue}
          onClose={closeDiffModal}
          submissionContextId={submissionContextId}
        />
      ) : null}

      <FieldHeader
        {...formFieldDefinition}
        label={formFieldDefinition.params.labels.main}
        isUpdated={isUpdated}
        editCallback={editCallback}
        onShowDiffModal={openDiffModal}
        availableActions={availableActions}
        entryPoint={entryPoint}
        isUpdateRequired={isUpdateRequired}
      />

      {value ? (
        <FocusIssueSelectDisplay
          value={selectedFocusIssue ? selectedFocusIssue.name : value}
          submissionContextId={submissionContextId}
           subtitle={formFieldDefinition.params.labels.options[selectedFocusIssue ? selectedFocusIssue.name : value]?.secondary!}
        />
      ) : (
        <FocusIssueSelectEmptyState />
      )}
    </Flex>
  );
};
